// frontend/src/components/Texpact/ShipmentList.jsx 

import React, { useState, useEffect } from 'react';
import { shipmentsAPI } from '../../api/client';
import DataTable from '../Common/DataTable';
import Pagination from '../Common/Pagination';
import Toast from '../Common/Toast';
import './ShipmentList.css';

// ─── Messages Modal ────────────────────────────────────────────────────────────
const MessagesModal = ({ shipment, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const response = await shipmentsAPI.getMessages(shipment.shipments_header_id);
        setMessages(response.data.data || []);
      } catch (err) {
        setError('Failed to load messages: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [shipment.shipments_header_id]);

  const statusBadge = (status, code) => {
    if (code === 201 || status === 'SUCCESS')
      return <span className="msg-badge msg-badge--success">✓ Success</span>;
    if (status === 'FAILED')
      return <span className="msg-badge msg-badge--failed">✗ Failed</span>;
    return <span className="msg-badge msg-badge--pending">⏳ Pending</span>;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="messages-modal" onClick={e => e.stopPropagation()}>
        <div className="messages-modal-header">
          <div>
            <h3>Messages</h3>
            <p className="messages-modal-subtitle">
              {shipment.shipment_code} &mdash; {shipment.po_code}
            </p>
          </div>
          <button className="messages-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="messages-modal-body">
          {loading && <div className="messages-empty">Loading…</div>}
          {error   && <div className="messages-empty messages-error">{error}</div>}
          {!loading && !error && messages.length === 0 && (
            <div className="messages-empty">No messages found for this shipment.</div>
          )}
          {!loading && !error && messages.length > 0 && (
            <table className="messages-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>API Code</th>
                  <th>API Status</th>
                  <th>Response</th>
                  <th>Created At</th>
                </tr>
              </thead>
              <tbody>
                {messages.map(msg => (
                  <tr key={msg.id}>
                    <td>{statusBadge(msg.status, msg.api_knitcloth_status_code)}</td>
                    <td>
                      {msg.api_knitcloth_status_code
                        ? <span className={msg.api_knitcloth_status_code === 201 ? 'success-code' : 'error-code'}>
                            {msg.api_knitcloth_status_code}
                          </span>
                        : '—'}
                    </td>
                    <td className="msg-api-status">{msg.api_knitcloth_status || '—'}</td>
                    <td className="msg-api-response">
                      {msg.api_knitcloth_response
                        ? (() => { try { return JSON.stringify(JSON.parse(msg.api_knitcloth_response)); } catch { return msg.api_knitcloth_response; } })()
                        : '—'}
                    </td>
                    <td className="msg-date">{new Date(msg.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── ShipmentList ──────────────────────────────────────────────────────────────
const ShipmentList = () => {
  const [toasts, setToasts] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resending, setResending] = useState(null);
  const [messagesModal, setMessagesModal] = useState(null);

  const addToast = (toast) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { ...toast, id }]);
  };
  const removeToast = (id) => setToasts(prev => prev.filter(t => t.id !== id));
  
  const [partnerFilter, setPartnerFilter] = useState('');
  const [supplierRestriction, setSupplierRestriction] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  
  useEffect(() => { loadShipments(); }, [partnerFilter, statusFilter, currentPage, itemsPerPage]);

  const loadShipments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (partnerFilter) params.append('partnerId', partnerFilter);
      if (statusFilter !== 'ALL') params.append('status', statusFilter);
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);
      const response = await shipmentsAPI.getAll(params.toString());
      if (response.data.success) {
        setShipments(response.data.data);
        setTotalPages(response.data.pagination.totalPages);
        setTotalItems(response.data.pagination.totalItems);
        if (response.data.supplierRestriction) setSupplierRestriction(response.data.supplierRestriction);
      } else if (Array.isArray(response.data)) {
        setShipments(response.data);
        setTotalPages(1);
        setTotalItems(response.data.length);
      }
      setError(null);
    } catch (err) {
      setError('Failed to load shipments: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const doResend = async (shipmentCode) => {
    try {
      setResending(shipmentCode);
      const response = await shipmentsAPI.resend(shipmentCode);
      if (response.data.success) {
        addToast({ type: 'success', message: `Message resent successfully! API status: ${response.data.apiStatus}` });
        loadShipments();
      } else {
        addToast({ type: 'error', message: `Failed to resend: ${response.data.message}` });
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      addToast({ type: 'error', message: `Error resending message: ${msg}` });
    } finally {
      setResending(null);
    }
  };

  const handleResend = (shipmentCode) => {
    addToast({
      type: 'warning',
      message: `Resend message for shipment ${shipmentCode}?`,
      requireConfirm: true,
      onConfirm: () => doResend(shipmentCode)
    });
  };

  const getStatusBadge = (shipmentStatus, messageStatus, apiStatusCode, messageCreatedAt, shipmentCreatedAt) => {
    const isResent = messageCreatedAt && shipmentCreatedAt &&
                     new Date(messageCreatedAt) > new Date(shipmentCreatedAt);
    if (apiStatusCode === 201)
      return <span className="status-badge status-success">✓ Sent {isResent && <small>(Resent)</small>}</span>;
    if (shipmentStatus === 'FAILED' || messageStatus === 'FAILED')
      return <span className="status-badge status-failed">✗ Failed</span>;
    if (shipmentStatus === 'PENDING')
      return <span className="status-badge status-pending">⏳ Pending</span>;
    if (shipmentStatus === 'SENT')
      return <span className="status-badge status-success">✓ Sent {isResent && <small>(Resent)</small>}</span>;
    return <span className="status-badge status-unknown">? Unknown</span>;
  };

  const canResend = (shipmentStatus, apiStatusCode) =>
    apiStatusCode !== 201 && (shipmentStatus === 'FAILED' || shipmentStatus === 'PENDING');

  const columns = [
    { header: 'Shipment Code', field: 'shipment_code', className: 'code-column', render: (row) => row.shipment_code },
    { header: 'PO Code',       field: 'po_code',       render: (row) => row.po_code },
    { header: 'Supplier',      field: 'supplier_name', render: (row) => row.supplier_name },
    { header: 'Date',          field: 'shipment_date', render: (row) => new Date(row.shipment_date).toLocaleDateString() },
    { header: 'Lines',         field: 'line_count',    className: 'text-center', render: (row) => row.line_count },
    { header: 'Total Qty',     field: 'total_shipped', className: 'text-right',  render: (row) => parseFloat(row.total_shipped || 0).toFixed(0) },
    {
      header: 'Status',
      field: 'shipment_status',
      render: (row) => getStatusBadge(row.shipment_status, row.message_status, row.api_knitcloth_status_code, row.message_created_at, row.created_at)
    },
    {
      header: 'API Response',
      field: 'api_knitcloth_status_code',
      render: (row) => row.api_knitcloth_status_code
        ? <span className={row.api_knitcloth_status_code === 201 ? 'success-code' : 'error-code'}>
            {row.api_knitcloth_status_code} - {row.api_knitcloth_status}
          </span>
        : <span className="no-response">No API response</span>
    },
    {
      header: 'Actions',
      field: 'actions',
      render: (row) => (
        <div className="action-buttons">
          <button className="btn-messages" onClick={() => setMessagesModal(row)} title="View messages">
            📨 Messages
          </button>
          {canResend(row.shipment_status, row.api_knitcloth_status_code) && (
            <button
              className="btn-resend"
              onClick={() => handleResend(row.shipment_code)}
              disabled={resending === row.shipment_code}
            >
              {resending === row.shipment_code ? '⏳ Sending…' : '↻ Resend'}
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="shipment-list">
      {/* Toast notifications */}
      <div className="toast-container">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            id={toast.id}
            type={toast.type}
            message={toast.message}
            requireConfirm={toast.requireConfirm}
            onConfirm={toast.onConfirm}
            onClose={removeToast}
          />
        ))}
      </div>

      <div className="shipment-filters">
        <div className="filters-row">
          {!supplierRestriction && (
            <div className="filter-group">
              <label>Partner</label>
              <select value={partnerFilter} onChange={(e) => { setPartnerFilter(e.target.value); setCurrentPage(1); }}>
                <option value="">All Partners</option>
                <option value="verdeveste-com">Verdeveste</option>
                <option value="costalongras">Costa Longras</option>
              </select>
            </div>
          )}
          <div className="filter-group">
            <label>Status</label>
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
              <option value="ALL">All Status</option>
              <option value="SENT">Sent</option>
              <option value="FAILED">Failed</option>
              <option value="PENDING">Pending</option>
            </select>
          </div>
          <button onClick={loadShipments} className="btn-refresh">🔄 Refresh</button>
        </div>
      </div>

      {!loading && !error && totalItems > 0 && (
        <div style={{ marginBottom: '12px', fontSize: '14px', color: 'var(--gray-600)', fontWeight: 500, padding: '0 4px' }}>
          Total: {totalItems} shipment{totalItems !== 1 ? 's' : ''}
        </div>
      )}

      <DataTable columns={columns} data={shipments} loading={loading} error={error} onRetry={loadShipments} emptyMessage="No shipments found" />

      {!loading && !error && totalItems > 0 && (
        <Pagination
          currentPage={currentPage} totalPages={totalPages} totalItems={totalItems}
          itemsPerPage={itemsPerPage} onPageChange={setCurrentPage}
          onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
        />
      )}

      {messagesModal && (
        <MessagesModal shipment={messagesModal} onClose={() => setMessagesModal(null)} />
      )}
    </div>
  );
};

export default ShipmentList;